// downloadLogsService.js - Frontend service for download logs API
// Handles API calls for download logs data

// Get API URL dynamically
const getApiUrl = () => {
  if (process.env.NODE_ENV === "production") {
    return "/api";
  }
  return "http://localhost:3001/api";
};

const API_BASE_URL = getApiUrl();

/**
 * Get authentication headers with token
 * @returns {Object} Headers object with authorization
 */
const getAuthHeaders = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${user.token || ""}`,
    // Bypass ngrok warning page
    "ngrok-skip-browser-warning": "true",
  };
};

/**
 * Handle API response and errors
 * @param {Response} response - Fetch response object
 * @returns {Object} Parsed response or error
 */
const handleApiResponse = async (response) => {
  try {
    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        message: data.message || "Erro na requisição",
        error: data.error || "Unknown error",
      };
    }

    return data;
  } catch (error) {
    return {
      success: false,
      message: "Erro ao processar resposta da API",
      error: error.message,
    };
  }
};

/**
 * Get download logs for a specific company
 * @param {number} companyId - Company ID
 * @param {Object} filters - Optional filters (date_from, date_to, status, limit)
 * @returns {Object} API response with company logs data
 */
const getCompanyDownloadLogs = async (companyId, filters = {}) => {
  try {
    // Build query string from filters
    const queryParams = new URLSearchParams();

    if (filters.date_from) queryParams.append("date_from", filters.date_from);
    if (filters.date_to) queryParams.append("date_to", filters.date_to);
    if (filters.status) queryParams.append("status", filters.status);
    if (filters.limit) queryParams.append("limit", filters.limit.toString());

    const queryString = queryParams.toString();
    const url = `${API_BASE_URL}/downloads/logs/${companyId}${
      queryString ? `?${queryString}` : ""
    }`;

    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    return await handleApiResponse(response);
  } catch (error) {
    console.error("Error in getCompanyDownloadLogs:", error);
    return {
      success: false,
      message: "Erro ao buscar logs de download da empresa",
      error: error.message,
    };
  }
};

/**
 * Get download logs summary for multiple companies
 * @param {Array} companyIds - Array of company IDs
 * @param {Object} filters - Optional filters (date_from, date_to)
 * @returns {Object} API response with multiple companies logs summary
 */
const getMultipleCompaniesLogs = async (companyIds, filters = {}) => {
  try {
    // Build query string from filters
    const queryParams = new URLSearchParams();

    if (filters.date_from) queryParams.append("date_from", filters.date_from);
    if (filters.date_to) queryParams.append("date_to", filters.date_to);

    const queryString = queryParams.toString();
    const url = `${API_BASE_URL}/downloads/logs/multiple${
      queryString ? `?${queryString}` : ""
    }`;

    const response = await fetch(url, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ companyIds }),
    });

    return await handleApiResponse(response);
  } catch (error) {
    console.error("Error in getMultipleCompaniesLogs:", error);
    return {
      success: false,
      message: "Erro ao buscar logs de múltiplas empresas",
      error: error.message,
    };
  }
};

/**
 * Get download logs statistics for a date range
 * @param {string} dateFrom - Start date (ISO format)
 * @param {string} dateTo - End date (ISO format)
 * @param {number|null} companyId - Optional company ID filter
 * @returns {Object} API response with statistics data
 */
const getDownloadLogsStatistics = async (
  dateFrom,
  dateTo,
  companyId = null
) => {
  try {
    // Build query string
    const queryParams = new URLSearchParams();
    queryParams.append("dateFrom", dateFrom);
    queryParams.append("dateTo", dateTo);

    if (companyId) queryParams.append("companyId", companyId.toString());

    const url = `${API_BASE_URL}/downloads/logs/statistics?${queryParams.toString()}`;

    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    return await handleApiResponse(response);
  } catch (error) {
    console.error("Error in getDownloadLogsStatistics:", error);
    return {
      success: false,
      message: "Erro ao buscar estatísticas de logs",
      error: error.message,
    };
  }
};

/**
 * Validate and format a specific download job
 * @param {number} jobId - Job ID
 * @returns {Object} API response with validated job data
 */
const validateJob = async (jobId) => {
  try {
    const url = `${API_BASE_URL}/downloads/logs/validate/${jobId}`;

    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    return await handleApiResponse(response);
  } catch (error) {
    console.error("Error in validateJob:", error);
    return {
      success: false,
      message: "Erro ao validar job",
      error: error.message,
    };
  }
};

/**
 * Start individual download for a specific company
 * @param {number} companyId - Company ID
 * @param {string} startDate - Start date (ISO format)
 * @param {string} endDate - End date (ISO format)
 * @param {boolean} forceDownloadPdf - Force PDF download even if exists (default: false)
 * @returns {Object} API response with job information
 */
const startIndividualDownload = async (
  companyId,
  startDate,
  endDate,
  forceDownloadPdf = false,
  options = {}
) => {
  try {
    const url = `${API_BASE_URL}/downloads/start-manual/${companyId}`;

    // options: { noteType, adnMode }. adnMode ("incremental" | "reprocess3m") só é
    // usado quando a empresa coleta via certificado (ADN, incremental por NSU).
    const response = await fetch(url, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        startDate,
        endDate,
        forceDownloadPdf,
        noteType: options.noteType,
        adnMode: options.adnMode,
      }),
    });

    return await handleApiResponse(response);
  } catch (error) {
    console.error("Error in startIndividualDownload:", error);
    return {
      success: false,
      message: "Erro ao iniciar download individual",
      error: error.message,
    };
  }
};

/**
 * Get download job status
 * @param {number} jobId - Job ID
 * @returns {Object} API response with job status
 */
const getDownloadJobStatus = async (jobId) => {
  try {
    const url = `${API_BASE_URL}/downloads/job/${jobId}/status`;

    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    return await handleApiResponse(response);
  } catch (error) {
    console.error("Error in getDownloadJobStatus:", error);
    return {
      success: false,
      message: "Erro ao obter status do job",
      error: error.message,
    };
  }
};

/**
 * Get notes for a specific company with optional filters
 * @param {number} companyId - Company ID
 * @param {Object} filters - Optional filters (date_from, date_to, limit, offset)
 * @returns {Object} API response with notes data
 */
const getCompanyNotes = async (companyId, filters = {}) => {
  try {
    // Build query string from filters
    const queryParams = new URLSearchParams();

    if (filters.date_from) queryParams.append("date_from", filters.date_from);
    if (filters.date_to) queryParams.append("date_to", filters.date_to);
    if (filters.limit) queryParams.append("limit", filters.limit.toString());
    if (filters.offset) queryParams.append("offset", filters.offset.toString());
    if (filters.note_type) queryParams.append("note_type", filters.note_type);

    const queryString = queryParams.toString();
    const url = `${API_BASE_URL}/downloads/companies/${companyId}/notes${
      queryString ? `?${queryString}` : ""
    }`;

    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    return await handleApiResponse(response);
  } catch (error) {
    console.error("Error in getCompanyNotes:", error);
    return {
      success: false,
      message: "Erro ao buscar notas da empresa",
      error: error.message,
    };
  }
};

/**
 * Export company notes to Excel format
 * @param {number} companyId - Company ID
 * @param {Object} filters - Optional filters (date_from, date_to)
 * @returns {Object} API response with download status
 */
const exportCompanyNotesToExcel = async (companyId, filters = {}) => {
  try {
    // Build query string from filters
    const queryParams = new URLSearchParams();

    if (filters.date_from) queryParams.append("date_from", filters.date_from);
    if (filters.date_to) queryParams.append("date_to", filters.date_to);
    if (filters.note_type) queryParams.append("note_type", filters.note_type);

    const queryString = queryParams.toString();
    const url = `${API_BASE_URL}/downloads/companies/${companyId}/notes/export${
      queryString ? `?${queryString}` : ""
    }`;

    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        message: errorData.message || "Erro ao exportar notas para Excel",
        error: errorData.error || "Unknown error",
      };
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get("Content-Disposition");
    let filename = "Relatorio_NFSe.xlsx";

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Convert response to blob
    const blob = await response.blob();

    // Create download link
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return {
      success: true,
      message: "Arquivo Excel baixado com sucesso",
      filename: filename,
    };
  } catch (error) {
    console.error("Error in exportCompanyNotesToExcel:", error);
    return {
      success: false,
      message: "Erro ao exportar notas para Excel",
      error: error.message,
    };
  }
};

/**
 * Comparação Hub × Portal Nacional (por emissão) para a empresa/período.
 * @param {number} companyId
 * @param {Object} filters - { date_from, date_to }
 * @returns {Object} { success, data }
 */
const getPortalComparison = async (companyId, filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (filters.date_from) queryParams.append("date_from", filters.date_from);
    if (filters.date_to) queryParams.append("date_to", filters.date_to);

    const queryString = queryParams.toString();
    const url = `${API_BASE_URL}/downloads/companies/${companyId}/portal-comparison${
      queryString ? `?${queryString}` : ""
    }`;

    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    return await handleApiResponse(response);
  } catch (error) {
    console.error("Error in getPortalComparison:", error);
    return {
      success: false,
      message: "Erro ao comparar Hub × Portal",
      error: error.message,
    };
  }
};

/**
 * Dispara a contagem AO VIVO do portal (rodapé) para a empresa/período.
 * @param {number} companyId
 * @param {Object} filters - { date_from, date_to }
 * @returns {Object} { success, data }
 */
const refreshPortalCount = async (companyId, filters = {}) => {
  try {
    const url = `${API_BASE_URL}/downloads/companies/${companyId}/portal-count`;

    const response = await fetch(url, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        date_from: filters.date_from,
        date_to: filters.date_to,
      }),
    });

    return await handleApiResponse(response);
  } catch (error) {
    console.error("Error in refreshPortalCount:", error);
    return {
      success: false,
      message: "Erro ao atualizar a contagem do portal",
      error: error.message,
    };
  }
};

/**
 * Format date for API calls (ISO format)
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
const formatDateForAPI = (date) => {
  if (!date) return null;

  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return null;

  return dateObj.toISOString().split("T")[0]; // YYYY-MM-DD format
};

/**
 * Format date for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date for display
 */
const formatDateForDisplay = (dateString) => {
  if (!dateString) return "N/A";

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      // hour: "2-digit",
      // minute: "2-digit",
    });
  } catch (error) {
    return "Data inválida";
  }
};

/**
 * Format duration in seconds to human readable format
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration
 */
const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return "N/A";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

/**
 * Get status color class for UI
 * @param {string} status - Status string
 * @returns {string} Tailwind CSS color class
 */
const getStatusColor = (status) => {
  const statusColors = {
    sync: "text-green-600 bg-green-50 border-green-200",
    with_failures: "text-orange-600 bg-orange-50 border-orange-200",
    discrepancy: "text-red-600 bg-red-50 border-red-200",
    error: "text-red-600 bg-red-50 border-red-200",
    completed: "text-green-600 bg-green-50 border-green-200",
    failed: "text-red-600 bg-red-50 border-red-200",
    running: "text-blue-600 bg-blue-50 border-blue-200",
    pending: "text-yellow-600 bg-yellow-50 border-yellow-200",
    cancelled: "text-gray-600 bg-gray-50 border-gray-200",
  };

  return statusColors[status] || statusColors.error;
};

/**
 * Get status icon for UI
 * @param {string} status - Status string
 * @returns {string} FontAwesome icon name
 */
const getStatusIcon = (status) => {
  const statusIcons = {
    sync: "check-circle",
    with_failures: "exclamation-triangle",
    discrepancy: "times-circle",
    error: "exclamation-circle",
    completed: "check-circle",
    failed: "times-circle",
    running: "spinner",
    pending: "clock",
    cancelled: "ban",
  };

  return statusIcons[status] || statusIcons.error;
};

/**
 * Format number with thousands separator
 * @param {number} number - Number to format
 * @returns {string} Formatted number
 */
const formatNumber = (number) => {
  if (typeof number !== "number" || isNaN(number)) return "0";
  return number.toLocaleString("pt-BR");
};

/**
 * Calculate success rate percentage
 * @param {number} successful - Number of successful items
 * @param {number} total - Total number of items
 * @returns {number} Success rate percentage
 */
const calculateSuccessRate = (successful, total) => {
  if (!total || total === 0) return 100;
  return ((successful / total) * 100).toFixed(1);
};

// Export service functions
export const downloadLogsService = {
  // API calls
  getCompanyDownloadLogs,
  getMultipleCompaniesLogs,
  getDownloadLogsStatistics,
  validateJob,
  startIndividualDownload,
  getDownloadJobStatus,
  getCompanyNotes,
  exportCompanyNotesToExcel,
  getPortalComparison,
  refreshPortalCount,

  // Utility functions
  formatDateForAPI,
  formatDateForDisplay,
  formatDuration,
  getStatusColor,
  getStatusIcon,
  formatNumber,
  calculateSuccessRate,
};

export default downloadLogsService;
